import ChevronRight from '@mui/icons-material/ChevronRight'
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { t } from 'i18next'
import { useModal } from 'mui-modal-provider'
import { NestedMenuItem } from 'mui-nested-menu'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { authApis } from '~/api'
import { useAppDispatch, useAppSelector } from '~/app/hooks'
import { ClientInstallerPopup, ConfirmationPopup } from '~/components'
import { PATH_NAME } from '~/constants'
import { IRefreshTokenForm } from '~/models'
import { logout, selectAuth } from '~/redux/features'
import { SwitchThemeForm } from './SwitchThemeForm'
import {
  EFDownloadIcon,
  SettingIcon,
  EFAppearanceIcon,
  EFLogoutIcon
} from '~/components/EFIcons'
import { useQueryClient } from 'react-query'

interface IUserDropdownMenuProps {
  anchorEl: HTMLElement | null
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>
}

export const UserDropdownMenu = ({ anchorEl, setAnchorEl }: IUserDropdownMenuProps) => {
  const AuthState = useAppSelector(selectAuth)
  const dispatch = useAppDispatch()
  const { showModal } = useModal()
  const navigate = useNavigate()
  const queryClient = useQueryClient();
  const [isOpenMenuTheme, setIsOpenMenuTheme] = React.useState(false)

  function handleClose() {
    setAnchorEl(null)
  }

  function handleOpenInstallerPopup() {
    showModal(ConfirmationPopup, {
      actionType: 'Close',
      children: <ClientInstallerPopup />,
      onClose: () => {},
      title: t('PageTitle_ClientInstaller'),
      width: '450px'
    })
  }

  async function handleLogout() {
    const refreshToken: IRefreshTokenForm = {
      userName: AuthState.userName,
      refreshToken: AuthState.refreshToken
    }

    try {
      await authApis.logoutUser(refreshToken);
      queryClient.removeQueries() // We purge the query cache just in case a user with different permissions logs in after
    } finally {
      dispatch(logout({ explicit: true }))
    }
  }

  return (
    <Menu
      id='user-menu'
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      MenuListProps={{ onMouseLeave: handleClose }}
    >
      <MenuItem
        sx={{ padding: 1 }}
        onClick={handleOpenInstallerPopup}
      >
        <ListItemIcon sx={{ justifyContent: 'center' }}>
          <EFDownloadIcon />
        </ListItemIcon>
        <ListItemText sx={{ minWidth: '14rem' }}>
          {t('PageTitle_ClientInstaller')}
        </ListItemText>
      </MenuItem>
      <MenuItem
        sx={{ padding: 1 }}
        onClick={() => navigate(PATH_NAME.PROFILE_SETTING.path)}
        selected={window.location.pathname.includes(PATH_NAME.PROFILE_SETTING.path)}
      >
        <ListItemIcon sx={{ justifyContent: 'center' }}>
          <SettingIcon />
        </ListItemIcon>
        <ListItemText>{t('PageTitle_ProfileSetting')}</ListItemText>
      </MenuItem>
      <NestedMenuItem
        MenuProps={{
          action(instance) {
            if (!instance) {
              setIsOpenMenuTheme(false)
              return
            }
            setIsOpenMenuTheme(true)
          },
          anchorReference: 'anchorEl',
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: {
            vertical: 'center',
            horizontal: 'right'
          },
          sx: {
            top: 18
          }
        }}
        leftIcon={<EFAppearanceIcon />}
        rightIcon={<ChevronRight />}
        label={t('Label_Appearance')}
        parentMenuOpen={true}
        sx={{ p: 1, paddingLeft: '18px', '& div': { alignItems: 'center' } }}
        onClick={event => event.stopPropagation()}
        className={isOpenMenuTheme ? 'hover' : ''}
      >
        <SwitchThemeForm />
      </NestedMenuItem>
      <MenuItem
        sx={{ padding: 1 }}
        onClick={handleLogout}
      >
        <ListItemIcon sx={{ justifyContent: 'center' }}>
          <EFLogoutIcon />
        </ListItemIcon>
        <ListItemText>{t('Link_Logout')}</ListItemText>
      </MenuItem>
    </Menu>
  )
}
